// In production, default to same-origin so nginx can proxy /api (required for public share links).
const _envApiUrl = process.env.REACT_APP_API_URL;
export const API_BASE = (_envApiUrl && _envApiUrl.trim())
  ? _envApiUrl.trim()
  : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

// ISSUE-029: Access token stored in memory only — never in localStorage.
// The httpOnly refresh cookie (sent automatically by the browser) is used
// to silently re-issue access tokens on page load via /auth/refresh.
let _accessToken = null;

function getToken() {
  return _accessToken;
}

function setToken(t) {
  _accessToken = t || null;
  // Remove any legacy localStorage token that may have been set before this fix
  localStorage.removeItem('auth_token');
}

// ── Silent refresh state ─────────────────────────────────────────────────────
// Ensures only one /auth/refresh call runs even when multiple requests 401 simultaneously.
let _refreshPromise = null;

async function _silentRefresh() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',   // send the HttpOnly refresh_token cookie
  })
    .then(res => {
      if (!res.ok) throw new Error('refresh_failed');
      return res.json();
    })
    .then(data => {
      setToken(data.access_token);
      return data.access_token;
    })
    .finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

// Exported so AppRouter can call it on startup to restore the session from
// the HttpOnly refresh cookie without requiring the user to log in again.
export async function tryRestoreSession() {
  try {
    await _silentRefresh();
    return true;
  } catch {
    return false;
  }
}

async function request(path, options = {}, _isRetry = false) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',   // always include cookies for refresh token
  });

  if (res.status === 401 && !_isRetry) {
    // Access token expired — attempt silent refresh once
    try {
      await _silentRefresh();
      // Retry the original request with the new access token
      return request(path, options, true);
    } catch {
      // Refresh also failed — session is fully expired
      setToken(null);
      throw new Error('Session expired. Please login again.');
    }
  }

  if (res.status === 401 && _isRetry) {
    setToken(null);
    throw new Error('Session expired. Please login again.');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export async function register(email, password) {
  const data = await request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(data.access_token);
  return data;
}

export async function login(email, password) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(data.access_token);
  return data;
}

export async function forgotPassword(email) {
  return request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token, password) {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export function logout() {
  const token = getToken();
  // Call backend to revoke refresh token + clear OAuth state
  if (token) {
    fetch(`${API_BASE}/api/oauth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).catch(() => {});
  }
  // Revoke refresh token cookie on backend
  fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => {});
  setToken(null);
  delete window._oauthStateMap;
}

export function isAuthenticated() {
  return !!getToken();
}

// ─── WebSocket ───────────────────────────────────────────────────────────────

export function getWsUrl(jobId, since = '0') {
  if (!jobId) return '';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // WS-TOKEN-MISSING-FIX: commit 291bbee ("security fixes", 2026-09-22) made
  // backend/api/websocket.py reject any handshake without `?token=<access
  // JWT>` (closed before accept -> HTTP 403), but this URL never carried a
  // token — so EVERY live-log connection was refused: no pipeline steps, no
  // logs, no progress in the UI, just an endless loader, while the job itself
  // kept running fine in the background. Browsers can't set an Authorization
  // header on a WebSocket, so the access token goes in the query string (the
  // exact contract the backend documents as ISSUE-006).
  const params = new URLSearchParams();
  if (since && since !== '0') params.set('since', since);
  const token = getToken();
  if (token) params.set('token', token);
  const qs = params.toString();
  return `${protocol}//${window.location.host}/ws/jobs/${jobId}/logs${qs ? `?${qs}` : ''}`;
}

// ─── Jobs ────────────────────────────────────────────────────────────────────

export async function listJobs(projectId = null) {
  const qs = projectId ? `?project_id=${projectId}` : '';
  return request(`/api/jobs${qs}`);
}

export async function getJobFiles(jobId) {
  return request(`/api/jobs/${jobId}/files`);
}

export async function getSecurityFindings(jobId) {
  return request(`/api/jobs/${jobId}/security-findings`);
}

export async function restoreFromJob(projectId, jobId) {
  return request(`/api/projects/${projectId}/restore-from-job/${jobId}`, { method: 'POST' });
}

// ─── GitHub / Repo Import ────────────────────────────────────────────────────

export async function importFromGitUrl(gitUrl, projectName, gitToken = '') {
  return request('/api/projects/import-git', {
    method: 'POST',
    body: JSON.stringify({ git_url: gitUrl, project_name: projectName, git_token: gitToken }),
  });
}

export async function getPushInfo(projectId) {
  return request(`/api/projects/${projectId}/push-info`);
}

export async function pushToGitHub(projectId, payload) {
  return request(`/api/projects/${projectId}/push`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function pushToProvider(projectId, payload) {
  return request(`/api/projects/${projectId}/push`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function importFromZip(file, projectName) {
  const token = getToken();
  const form = new FormData();
  form.append('file', file);
  form.append('project_name', projectName);
  const res = await fetch(`${API_BASE}/api/projects/import`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (res.status === 401) { setToken(null); throw new Error('Session expired. Please login again.'); }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Import failed');
  }
  return res.json();
}

// ─── File/Folder CRUD ────────────────────────────────────────────────────────

export async function createFileOrFolder(projectId, path, content = '', isFolder = false) {
  return request(`/api/projects/${projectId}/files/create`, {
    method: 'POST',
    body: JSON.stringify({ path, content, is_folder: isFolder }),
  });
}

export async function renameFileOrFolder(projectId, oldPath, newPath) {
  return request(`/api/projects/${projectId}/files/rename`, {
    method: 'POST',
    body: JSON.stringify({ old_path: oldPath, new_path: newPath }),
  });
}

export async function deleteFileOrFolder(projectId, path, isFolder = false) {
  return request(`/api/projects/${projectId}/files/delete`, {
    method: 'POST',
    body: JSON.stringify({ path, is_folder: isFolder }),
  });
}

export async function moveFile(projectId, sourcePath, destinationFolder) {
  return request(`/api/projects/${projectId}/files/move`, {
    method: 'POST',
    body: JSON.stringify({ source_path: sourcePath, destination_folder: destinationFolder }),
  });
}

export async function updateFileContent(projectId, path, content) {
  return request(`/api/projects/${projectId}/files/update`, {
    method: 'PUT',
    body: JSON.stringify({ path, content, is_folder: false }),
  });
}

// ─── Authenticated Fetch Helper ──────────────────────────────────────────────
// Mirrors the silent-refresh logic in request() so callers that use authFetch
// directly (useOnboarding, settingsService, ProjectList, etc.) also recover
// from an expired access token without showing ERR_EMPTY_RESPONSE.

export async function authFetch(path, options = {}, _isRetry = false) {
  const token = getToken();
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });

  if (res.status === 401 && !_isRetry) {
    try {
      await _silentRefresh();
      return authFetch(path, options, true);
    } catch {
      setToken(null);
      // Return a synthetic 401 response so callers that check res.ok still work
      return new Response(JSON.stringify({ detail: 'Session expired. Please login again.' }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' },
      });
    }
  }

  return res;
}

// ─── Project sharing ─────────────────────────────────────────────────────────

export function buildSharePageUrl(shareToken) {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/shared/${shareToken}`;
  }
  return `/shared/${shareToken}`;
}

export async function getProjectShareStatus(projectId) {
  return request(`/api/projects/${projectId}/share`);
}

export async function enableProjectShare(projectId) {
  return request(`/api/projects/${projectId}/share`, { method: 'POST' });
}

export async function disableProjectShare(projectId) {
  return request(`/api/projects/${projectId}/share`, { method: 'DELETE' });
}

export async function fetchSharedProject(token) {
  const res = await fetch(`${API_BASE}/api/shared/${encodeURIComponent(token)}`);
  if (res.status === 404) {
    const err = await res.json().catch(() => ({ detail: 'Shared project not found' }));
    const e = new Error(err.detail || 'Shared project not found');
    e.code = 'NOT_FOUND';
    throw e;
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Failed to load shared project');
  }
  return res.json();
}

export function resolveSharedPreviewUrl(previewUrl) {
  if (!previewUrl) return null;
  if (/^https?:\/\//i.test(previewUrl)) return previewUrl;
  const base = API_BASE || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base.replace(/\/$/, '')}${previewUrl.startsWith('/') ? '' : '/'}${previewUrl}`;
}

// ─── Domains ─────────────────────────────────────────────────────────────────

export async function listDomains(jobId) {
  return request(`/api/deployments/${jobId}/domains`);
}

export async function getManagedSubdomain(jobId) {
  return request(`/api/deployments/${jobId}/managed-subdomain`);
}

export async function addDomain(jobId, domain, redirectMode = null) {
  return request(`/api/deployments/${jobId}/domains`, {
    method: 'POST',
    body: JSON.stringify({ domain, redirect_mode: redirectMode }),
  });
}

export async function removeDomain(jobId, domainId) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/api/deployments/${jobId}/domains/${domainId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Remove domain failed');
  }
}

export async function verifyDomain(jobId, domainId) {
  return request(`/api/deployments/${jobId}/domains/${domainId}/verify`, { method: 'POST' });
}

export async function getDomainStatus(jobId, domainId) {
  return request(`/api/deployments/${jobId}/domains/${domainId}/status`);
}

export async function setPrimaryDomain(jobId, domainId) {
  return request(`/api/deployments/${jobId}/domains/${domainId}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_primary: true }),
  });
}

export async function setDomainRedirect(jobId, domainId, redirectMode) {
  return request(`/api/deployments/${jobId}/domains/${domainId}`, {
    method: 'PATCH',
    body: JSON.stringify({ redirect_mode: redirectMode }),
  });
}

// ── Collaboration ─────────────────────────────────────────────────────────────

export async function getCollabMembers(projectId) {
  return request(`/api/collab/${projectId}/members`);
}

export async function inviteMember(projectId, email, role = 'editor') {
  return request(`/api/collab/${projectId}/invite`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

export async function updateMemberRole(projectId, userId, role) {
  return request(`/api/collab/${projectId}/members/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export async function removeMember(projectId, userId) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/api/collab/${projectId}/members/${userId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Remove member failed');
  }
}

export async function getMyInvitations() {
  return request('/api/collab/invitations/mine');
}

export async function acceptInvitationById(invitationId) {
  return request(`/api/collab/invitations/${invitationId}/accept`, { method: 'POST' });
}

export async function rejectInvitationById(invitationId) {
  return request(`/api/collab/invitations/${invitationId}/reject`, { method: 'POST' });
}

export async function searchUsers(query) {
  return request(`/api/collab/users/search?q=${encodeURIComponent(query)}`);
}

export async function getCollabActivity(projectId, limit = 50) {
  return request(`/api/collab/${projectId}/activity?limit=${limit}`);
}

export async function getCollabPresence(projectId) {
  return request(`/api/collab/${projectId}/presence`);
}

export async function requestAiDiff(projectId, jobId, files) {
  return request(`/api/collab/${projectId}/ai-diff`, {
    method: 'POST',
    body: JSON.stringify({ job_id: jobId, files }),
  });
}

export async function applyAiChanges(projectId, jobId, files) {
  return request(`/api/collab/${projectId}/ai-apply`, {
    method: 'POST',
    body: JSON.stringify({ job_id: jobId, files }),
  });
}

export function getCollabWsUrl(projectId) {
  const wsOverride = process.env.REACT_APP_WS_URL;
  if (wsOverride) {
    return `${wsOverride.replace(/\/$/, '')}/ws/collab/${projectId}`;
  }
  try {
    const apiUrl = new URL(API_BASE);
    const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${apiUrl.host}/ws/collab/${projectId}`;
  } catch {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws/collab/${projectId}`;
  }
}

export async function retryDomain(jobId, domainId) {
  return request(`/api/deployments/${jobId}/domains/${domainId}/retry`, { method: 'POST' });
}

// ─── Flat Domain endpoints (spec-compliant) ───────────────────────────────────

export async function listAllDomains(projectId = null) {
  const qs = projectId ? `?project_id=${projectId}` : '';
  return request(`/api/domains${qs}`);
}

export async function getDomainById(domainId) {
  return request(`/api/domains/${domainId}`);
}

export async function addDomainFlat(projectId, domain, redirectMode = null) {
  return request('/api/domains', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, domain, redirect_mode: redirectMode }),
  });
}

export async function getDomainStatusFlat(domainId) {
  return request(`/api/domains/${domainId}/status`);
}

export async function verifyDomainFlat(domainId) {
  return request(`/api/domains/${domainId}/verify`, { method: 'POST' });
}

export async function retryDomainFlat(domainId) {
  return request(`/api/domains/${domainId}/retry`, { method: 'POST' });
}

export async function removeDomainFlat(domainId) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/api/domains/${domainId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Remove domain failed');
  }
}
